import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { SCADA_MONITORING, isScadaStationPath, useScadaStationRoute } from '@/scada/useScadaStation';
import { useTheme } from '@/hooks/useTheme';
import {
  Crown, BarChart3, BatteryCharging, Battery, BookOpen, Briefcase, Calculator,
  ClipboardList, Cpu, Database, SlidersHorizontal, ArrowLeftRight, FileText, Zap,
  Ban, TrendingUp, Activity, Landmark, History, Home, LineChart, Mail,
  CircleDollarSign, Newspaper, Network, Bell, BellRing, Plug, Router, Send,
  ShieldCheck, Repeat, SquareTerminal, UserCog, type LucideIcon,
} from 'lucide-react';
import { hasArtemKView } from '@/utils/artemkView';
import { viewGroupOf } from '@/utils/viewGroups';

// «Моніторинг обладнання» веде на картку ПОТОЧНОЇ СЕС — тієї, що вибрана
// глобальним селектором у топбарі. У списку меню стоїть сентинел
// SCADA_MONITORING, а конкретний маршрут підставляється під час кліку
// (див. handleNav) з useScadaStationRoute. Хардкоду станції немає.

// Мапа Remix → Lucide для іконок меню (єдиний інженерний лінійний набір).
const LUCIDE_NAV: Record<string, LucideIcon> = {
  'ri-vip-crown-line': Crown, 'ri-bar-chart-2-line': BarChart3,
  'ri-battery-2-charge-line': BatteryCharging, 'ri-battery-charge-line': Battery,
  'ri-battery-share-line': BatteryCharging, 'ri-book-2-line': BookOpen,
  'ri-briefcase-line': Briefcase, 'ri-calculator-line': Calculator,
  'ri-clipboard-line': ClipboardList, 'ri-cpu-line': Cpu, 'ri-database-2-line': Database,
  'ri-equalizer-line': SlidersHorizontal, 'ri-exchange-dollar-line': ArrowLeftRight,
  'ri-file-list-3-line': FileText, 'ri-file-paper-2-line': FileText,
  'ri-flashlight-line': Zap, 'ri-forbid-2-line': Ban, 'ri-funds-line': TrendingUp,
  'ri-git-commit-line': Activity, 'ri-government-line': Landmark, 'ri-history-line': History,
  'ri-home-4-line': Home, 'ri-line-chart-line': LineChart, 'ri-mail-line': Mail,
  'ri-money-dollar-circle-line': CircleDollarSign, 'ri-newspaper-line': Newspaper,
  'ri-node-tree': Network, 'ri-notification-3-line': Bell, 'ri-notification-badge-line': BellRing,
  'ri-plug-line': Plug, 'ri-router-line': Router, 'ri-send-plane-line': Send,
  'ri-shield-keyhole-line': ShieldCheck, 'ri-swap-box-line': Repeat,
  'ri-terminal-box-line': SquareTerminal, 'ri-user-settings-line': UserCog,
};

interface NavItem {
  icon: string;
  label: string;
  path?: string;
  badge?: string;
  badgeType?: 'new' | 'trial' | 'live' | 'hot' | 'count';
  /** Інший застосунок під своїм базовим шляхом (напр. стенд /tg/):
   *  потрібне повне перезавантаження, бо react-router його не маршрутизує. */
  external?: boolean;
  locked?: boolean;
  count?: number;
  /** Вкладені пункти — рендеряться акордеоном під батьківським пунктом. */
  children?: NavItem[];
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const OWNER_NAV: NavSection[] = [
  {
    items: [
      { icon: 'ri-vip-crown-line', label: 'nav_owner_home', path: '/dashboard/owner' },
    ],
  },
  {
    title: 'nav_gen',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'nav_forecast', path: '/forecast', badge: 'LIVE', badgeType: 'live' },
      { icon: 'ri-database-2-line', label: 'nav_disp_fact', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-git-commit-line', label: 'nav_gen_deviation', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_finance',
    items: [
      // Тестовий стенд: у прод-збірці VITE_FEATURE_FINANCIAL_RESULT відсутній,
      // тож пункти зникають (як і forecast-mms). Модуль «Фінансовий результат».
      ...(import.meta.env.VITE_FEATURE_FINANCIAL_RESULT === '1'
        ? [{ icon: 'ri-line-chart-line', label: 'nav_fin_result', path: '/financial/result' },
           { icon: 'ri-book-2-line', label: 'nav_fin_sale_terms', path: '/financial/sale-terms' }]
        : []),
      { icon: 'ri-money-dollar-circle-line', label: 'nav_fin_income', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-calculator-line', label: 'nav_fin_charges', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-battery-share-line', label: 'nav_fin_bess_arb', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_analytics',
    items: [
      { icon: 'ri-exchange-dollar-line', label: 'nav_an_market', path: '/market/ueex' },
      { icon: 'ri-newspaper-line', label: 'nav_an_news', path: '/info', badge: 'AI', badgeType: 'live' },
    ],
  },
  {
    items: [
      { icon: 'ri-notification-3-line', label: 'nav_notifications', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
];

const FULL_NAV: NavSection[] = [
  {
    items: [
      { icon: 'ri-home-4-line', label: 'nav_dashboard', path: '/' },
    ],
  },
  {
    title: 'nav_gen',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'nav_forecast', path: '/forecast', badge: 'LIVE', badgeType: 'live' },
      { icon: 'ri-database-2-line', label: 'nav_disp_fact', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-git-commit-line', label: 'nav_gen_deviation', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_market',
    items: [
      { icon: 'ri-funds-line', label: 'nav_market_prices', path: '/market/prices' },
    ],
  },
  {
    title: 'nav_dispatch2',
    items: [
      { icon: 'ri-line-chart-line', label: 'nav_trade_schedule', path: '/trading' },
      // Тестовий стенд: у прод-збірці VITE_FEATURE_FORECAST_MMS відсутній,
      // тому пункт просто не потрапляє в меню.
      ...(import.meta.env.VITE_FEATURE_FORECAST_MMS === '1'
        ? [{ icon: 'ri-node-tree', label: 'nav_forecast_mms', path: '/dispatch/forecast-mms' },
           // Макет торгового графіка на обговорення з Вадимом, Олею та
           // технічним диспетчером. Підпис літеральний: назва тимчасова й
           // перекладати її ще нема сенсу.
           { icon: 'ri-draft-line', label: 'Торговий графік (v.1)', path: '/dispatch/schedule-draft', badge: 'МАКЕТ', badgeType: 'new' as const },
           // Друга версія макета — ОКРЕМИЙ застосунок на стенді /tg/
           // (гілка feature/trading-schedule-mockup, свій контейнер і
           // Traefik-правило). Звідси лише посилання: спільного роутера в
           // них немає, тож перехід має бути повним.
           { icon: 'ri-draft-line', label: 'Торговий графік (v.2)', path: '/tg/dispatch/trading-schedule', external: true, badge: 'МАКЕТ', badgeType: 'new' as const }]
        : []),
      ...(import.meta.env.VITE_FEATURE_EMS === '1'
        ? [{ icon: 'ri-cpu-line', label: 'EMS Box', path: '/ems' }]
        : []),
      { icon: 'ri-terminal-box-line', label: 'nav_disp_commands', path: '/sko' },
    ],
  },
  {
    title: 'nav_sec_finance',
    items: [
      // Тестовий стенд: у прод-збірці VITE_FEATURE_FINANCIAL_RESULT відсутній,
      // тож пункти зникають (як і forecast-mms). Модуль «Фінансовий результат».
      ...(import.meta.env.VITE_FEATURE_FINANCIAL_RESULT === '1'
        ? [{ icon: 'ri-line-chart-line', label: 'nav_fin_result', path: '/financial/result' },
           { icon: 'ri-book-2-line', label: 'nav_fin_sale_terms', path: '/financial/sale-terms' }]
        : []),
      { icon: 'ri-money-dollar-circle-line', label: 'nav_fin_income', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-calculator-line', label: 'nav_fin_charges', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-battery-share-line', label: 'nav_fin_bess_arb', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    // У сайдбарі — лише великі розділи SCADA. «Ручне керування» і «Події»
    // звідси прибрані: вони вже є кнопками у внутрішній навігації SCADA
    // (scada/navConfig.ts). Два рівні навігації не дублюємо. Маршрути
    // /scada/control і /scada/events лишились зареєстрованими й робочими.
    title: 'nav_disp_scada',
    items: [
      { icon: 'ri-cpu-line', label: 'nav_scada_monitoring', path: SCADA_MONITORING, badge: 'PREVIEW', badgeType: 'new' },
      { icon: 'ri-router-line', label: 'nav_scada_telemetry', path: '/scada/emsbox' },
    ],
  },
  {
    title: 'nav_bess',
    items: [
      { icon: 'ri-battery-charge-line', label: 'nav_bess_charge', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-flashlight-line', label: 'nav_bess_discharge', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-swap-box-line', label: 'nav_bess_arbitrage', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_assets',
    items: [
      { icon: 'ri-flashlight-line', label: 'nav_assets_list', path: '/assets' },
    ],
  },
  {
    title: 'nav_incidents',
    items: [
      { icon: 'ri-alarm-warning-line', label: 'nav_inc_faults', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-forbid-2-line', label: 'nav_inc_limits', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-notification-badge-line', label: 'nav_inc_alarms', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_analytics',
    items: [
      { icon: 'ri-exchange-dollar-line', label: 'nav_an_market', path: '/market/ueex' },
      { icon: 'ri-newspaper-line', label: 'nav_an_news', path: '/info', badge: 'AI', badgeType: 'live' },
    ],
  },
  {
    title: 'nav_reports',
    items: [
      { icon: 'ri-government-line', label: 'nav_rep_regulatory', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-briefcase-line', label: 'nav_rep_management', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-clipboard-line', label: 'nav_rep_dispatch', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_documents',
    items: [
      { icon: 'ri-file-list-3-line', label: 'nav_doc_contracts', path: '/contracts' },
      { icon: 'ri-file-paper-2-line', label: 'nav_doc_acts', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-mail-line', label: 'nav_doc_correspondence', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_settings',
    items: [
      { icon: 'ri-user-settings-line', label: 'nav_set_users', path: '/admin/users' },
      { icon: 'ri-shield-keyhole-line', label: 'nav_set_roles', path: '/admin/roles' },
      { icon: 'ri-plug-line', label: 'nav_set_integrations', path: '/settings' },
    ],
  },
];

// Розширення меню власника для ArtemK (CEO): додаткові пункти EMS Box та блок
// SCADA, яких немає в базовому OWNER_NAV. Точково за логіном — інших CEO не чіпає.
const OWNER_EXTRA_ARTEMK: NavSection[] = [
  ...(import.meta.env.VITE_FEATURE_EMS === '1'
    ? [{ items: [{ icon: 'ri-cpu-line', label: 'EMS Box', path: '/ems' }] }]
    : []),
  {
    // У сайдбарі — лише великі розділи SCADA. «Ручне керування» і «Події»
    // звідси прибрані: вони вже є кнопками у внутрішній навігації SCADA
    // (scada/navConfig.ts). Два рівні навігації не дублюємо. Маршрути
    // /scada/control і /scada/events лишились зареєстрованими й робочими.
    title: 'nav_disp_scada',
    items: [
      { icon: 'ri-cpu-line', label: 'nav_scada_monitoring', path: SCADA_MONITORING, badge: 'PREVIEW', badgeType: 'new' },
      { icon: 'ri-router-line', label: 'nav_scada_telemetry', path: '/scada/emsbox' },
    ],
  },
];

// ArtemK: компактне меню рівно з 6 пунктів, без заголовків секцій і без
// акордеонів — Головна, Керування, Фінанси, Документи, Контакти, Інфо.
// Операційні розділи (Диспетчерська, Прогноз, Ціни, Торговий графік) входом у
// меню не мають: вони перемикаються рядом кнопок під шапкою «Фінансів».
// Свідомо прибрані «Перелік СЕС», «Телеметрія», «EMS Box» і «Довідник умов»;
// «Новини» теж не в меню — їхній вміст показується під контентом головної.
const ARTEMK_NAV: NavSection[] = [
  {
    items: [
      { icon: 'ri-home-4-line', label: 'nav_home', path: '/' },
      // Диспетчерської в меню немає ЗОВСІМ: ні акордеона, ні окремого пункту
      // «Оптимізація». Усі ці розділи — Диспетчерська, Прогноз, Ціни, Торговий
      // графік — живуть рядом кнопок під шапкою «Фінансів» (OptSectionTabs),
      // тож власний пункт меню був другим входом у той самий ряд.
      // Маршрути лишились робочими: на /dispatch/forecast-mms веде кнопка
      // «Диспетчерська» того ряду, і сторінка повністю доступна за адресою.
      // «Керування» — колишній «Моніторинг обладнання» з акордеона, до 05.10
      // підписаний як «SCADA». Підпис літеральний, а не ключ i18n: назва
      // стосується лише цього кабінету, в інших акаунтах розділ лишається
      // «SCADA» (див. nav_disp_scada). Та сама назва стоїть у breadcrumb
      // сторінок SCADA для ArtemK — ScadaLayout.
      { icon: 'ri-cpu-line', label: 'ak_nav_control', path: SCADA_MONITORING, badge: 'PREVIEW', badgeType: 'new' },
      // «Фінанси» ведуть одразу на «Фінансовий результат»; на проді модуля
      // немає, тож пункт зникає разом із прапорцем.
      ...(import.meta.env.VITE_FEATURE_FINANCIAL_RESULT === '1'
        ? [{ icon: 'ri-money-dollar-circle-line', label: 'nav_sec_finance', path: '/financial/result' }]
        : []),
      { icon: 'ri-file-list-3-line', label: 'nav_documents', path: '/docs' },
      // Окремої сторінки контактів у застосунку немає: вони живуть вкладкою
      // всередині картки контрагента, тож пункт веде на список контрагентів.
      { icon: 'ri-mail-line', label: 'nav_contacts', path: '/contacts' },
      { icon: 'ri-newspaper-line', label: 'nav_info', path: '/about' },
    ],
  },
];

// Спеціальний акаунт СЕС Табаки (login 'tabaki', scope=лише Табаки): рівно 6
// пунктів — Дашборд, Прогноз, Фінансовий результат, Довідник умов, EMS Box, SCADA.
// Дані вже обмежені scope-ом на бекенді, тож усе показує тільки по Табаки.
// Технічний диспетчер (акаунт `dispatch`): дашборд парку + SCADA.
// Головна лишається портфельним дашбордом усього парку (portfolioHome.ts)
// і відкривається при вході; у меню її свідомо немає — так попросив
// користувач 14.10.
const DISPATCH_NAV: NavSection[] = [
  { items: [{ icon: 'ri-home-4-line', label: 'nav_dashboard', path: '/' }] },
  {
    title: 'nav_disp_scada',
    items: [
      // SCADA_MONITORING — сентинел, а не адреса: клік веде на картку ПОТОЧНОЇ
      // станції (/scada/station/:id/overview), як у volodp з повним меню.
      // Станцію підставляє селектор у топбарі, а він сам обирає першу зі
      // скоупу акаунта. Переліку /scada/dispatch диспетчер не бачить.
      { icon: 'ri-cpu-line', label: 'SCADA', path: SCADA_MONITORING },
    ],
  },
];

const TABAKI_NAV: NavSection[] = [
  { items: [{ icon: 'ri-home-4-line', label: 'nav_dashboard', path: '/' }] },
  {
    title: 'nav_gen',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'nav_forecast', path: '/forecast', badge: 'LIVE', badgeType: 'live' },
    ],
  },
  {
    title: 'nav_sec_finance',
    items: [
      ...(import.meta.env.VITE_FEATURE_FINANCIAL_RESULT === '1'
        ? [{ icon: 'ri-line-chart-line', label: 'nav_fin_result', path: '/financial/result' },
           { icon: 'ri-book-2-line', label: 'nav_fin_sale_terms', path: '/financial/sale-terms' }]
        : []),
    ],
  },
  {
    title: 'Тех. Диспетчерська',
    items: [
      ...(import.meta.env.VITE_FEATURE_EMS === '1'
        ? [{ icon: 'ri-cpu-line', label: 'EMS Box', path: '/ems' }]
        : []),
      { icon: 'ri-router-line', label: 'SCADA', path: '/scada/dispatch' },
    ],
  },
];


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  isMobile: boolean;
  language?: string;
}

export default function Sidebar({ collapsed: collapsedProp, onToggle, mobileOpen, onMobileClose, isMobile }: SidebarProps) {
  // Редизайн: сайдбар згорнутий постійно (rail), розкривається на hover як
  // overlay поверх контенту (ширина в лейауті лишається 60 — екран не зсувається).
  const isRd = import.meta.env.VITE_REDESIGN === '1';
  const [hovered, setHovered] = useState(false);
  const { isDark } = useTheme();
  const collapsed = isRd ? !hovered : collapsedProp;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { user } = useAuth();

  const displayName = user?.displayName ?? t('ak_nav_user');
  const role = user ? `${user.role} · ${user.company}` : '';
  const avatarLetters = user?.avatar ?? '?';

  // Спеціальний акаунт Табаки — власне 6-пунктове меню; далі: CEO -> owner menu,
  // решта -> full menu (per Меню_*.docx).
  // Меню визначає ГРУПА інтерфейсу (utils/viewGroups.ts), а не перелік
  // логінів у кожному місці: акаунт належить рівно одній групі.
  const group = viewGroupOf(user);
  const allSections = group === 'station'
    ? TABAKI_NAV
    : group === 'scada'
    ? DISPATCH_NAV
    : group === 'compact'
    ? ARTEMK_NAV
    : user?.role === 'CEO'
    ? OWNER_NAV
    : FULL_NAV;

  // Недоступні пункти (locked) не рендеряться зовсім — ні сірими, ні
  // напівпрозорими: користувач не має бачити те, чого не може відкрити.
  // Фільтр один на всі меню (Табаки / CEO / повне), тож жоден locked не
  // просочиться. Записи лишаються в конфігурації як дорожня карта: щоб
  // увімкнути розділ, досить зняти прапорець.
  // Категорія без жодного доступного пункту зникає разом із заголовком.
  const sections = allSections
    .map((section) => ({
      ...section,
      items: (section.items ?? [])
        .filter((it: NavItem) => !it.locked)
        // Той самий фільтр усередині акордеонів, інакше locked-пункт
        // просочився б через вкладений рівень.
        .map((it: NavItem) => (it.children
          ? { ...it, children: it.children.filter((c) => !c.locked) }
          : it))
        // Акордеон, що лишився без жодного доступного пункту, не показуємо.
        .filter((it: NavItem) => !it.children || it.children.length > 0),
    }))
    .filter((section) => section.items.length > 0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getBadgeStyle = (type?: string) => {
    switch (type) {
      case 'new': return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
      case 'trial': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'live': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'hot': return 'bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--line-strong)]';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Маршрут «Моніторингу» для поточної станції. Джерело — глобальний селектор.
  const scadaStationRoute = useScadaStationRoute();

  const isActive = (path?: string) => {
    if (!path) return false;
    // Пункт «Моніторинг обладнання» підсвічений на картці будь-якої станції.
    if (path === SCADA_MONITORING) return isScadaStationPath(location.pathname);
    return location.pathname === path;
  };

  // Розгорнуті акордеони. Група з активним підпунктом відкрита завжди —
  // інакше після переходу користувач не бачив би, де він перебуває.
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  const isGroupOpen = (item: NavItem) =>
    openGroups.includes(item.label) || (item.children ?? []).some((c) => isActive(c.path));

  const handleNav = (path?: string, external?: boolean) => {
    if (!path) return;
    // Інший застосунок (стенд /tg/) живе поза нашим роутером — тільки
    // повним переходом, інакше SPA віддасть сторінку «не знайдено».
    if (external) { window.location.assign(path); return; }
    // Сентинел розгортається в картку станції, вибраної селектором.
    navigate(path === SCADA_MONITORING ? scadaStationRoute : path);
    if (isMobile) onMobileClose();
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        onMouseEnter={() => { if (isRd) setHovered(true); }}
        onMouseLeave={() => { if (isRd) setHovered(false); }}
        className="fixed left-0 top-0 h-screen flex flex-col z-30 hidden md:flex"
        style={{
          width: collapsed ? '60px' : (isRd ? '224px' : '220px'),
          // Матове тло (frosted): у темній темі напівпрозоре 0.72 (blur показує
          // темний контент), у СВІТЛІЙ — щільне темне 0.97, щоб панель лишалась
          // у кольорі темної теми (інакше blur світлого тла робив її сірою).
          background: isRd ? (isDark ? 'rgba(31, 39, 47, 0.72)' : 'rgba(31, 39, 47, 0.97)') : '#1A1A1A',
          backdropFilter: isRd ? 'blur(16px) saturate(120%)' : undefined,
          WebkitBackdropFilter: isRd ? 'blur(16px) saturate(120%)' : undefined,
          borderRight: '1px solid rgba(255,255,255,0.08)',
          boxShadow: isRd && !collapsed ? '12px 0 40px rgba(0,0,0,0.32)' : undefined,
          // Плавне, повільніше розкриття: довший ease-out.
          transition: 'width 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease',
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center justify-center h-[72px] shrink-0 relative px-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          {collapsed ? (
            <img
              src="/helios-logo-white.png"
              alt="Helios AG"
              className="h-12 w-auto object-contain"
            />
          ) : (
            <img
              src="/helios-logo-white.png"
              alt="Helios AG"
              className="h-12 w-auto object-contain"
            />
          )}
          {!isRd && (
          <button
            onClick={onToggle}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded cursor-pointer transition-colors"
            style={{ color: '#6B6B70' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B6B70'; }}
          >
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-base`}></i>
          </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin" style={{ scrollbarWidth: 'none' }}>
          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1' : ''}>
              {section.title && !collapsed && (
                <div className="px-4 py-2 text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#4A4A50' }}>
                  {t(section.title as string)}
                </div>
              )}
              {section.title && collapsed && <div className="my-1 mx-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}></div>}
              {section.items.flatMap((item) => {
                // Акордеон: батьківський пункт + вкладені, коли розгорнуто.
                // У згорнутому рейлі підпунктів не видно, тож клік по
                // батьківському веде на перший доступний з них.
                const group = item.children && item.children.length > 0;
                const open = group ? isGroupOpen(item) : false;
                const rows = group && open && !collapsed
                  ? [{ item, child: false }, ...item.children!.map((c) => ({ item: c, child: true }))]
                  : [{ item, child: false }];

                return rows.map(({ item: row, child }) => {
                const active = isActive(row.path) || (row === item && group && !open
                  && (item.children ?? []).some((c) => isActive(c.path)));
                const isGroupRow = row === item && group;
                return (
                  <button
                    key={(child ? 'c:' : '') + row.label}
                    onClick={() => {
                      if (isGroupRow) {
                        if (collapsed) handleNav(item.children![0].path, item.children![0].external);
                        else toggleGroup(item.label);
                        return;
                      }
                      handleNav(row.path, row.external);
                    }}
                    onMouseEnter={() => setHoveredItem(row.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                    disabled={row.locked}
                    className="w-full flex items-center text-left transition-all duration-150 cursor-pointer relative group"
                    style={{
                      padding: collapsed ? '8px 0' : `7px 12px 7px ${child ? 30 : 14}px`,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
                      background: active
                        ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                        : hoveredItem === row.label && !row.locked
                        ? 'rgba(255,255,255,0.05)'
                        : 'transparent',
                      opacity: row.locked ? 0.45 : 1,
                    }}
                  >
                    <div className={`flex items-center justify-center shrink-0 ${isRd && collapsed ? 'w-6 h-6' : 'w-4 h-4'}`}>
                      {isRd && LUCIDE_NAV[row.icon] ? (
                        (() => {
                          const Ic = LUCIDE_NAV[row.icon];
                          return <Ic size={collapsed ? 21 : 17} strokeWidth={1.75}
                            style={{ color: active ? 'var(--accent)' : row.locked ? '#4A4A50' : '#A0A0A8' }} />;
                        })()
                      ) : (
                        <i
                          className={`${row.icon} ${isRd && collapsed ? 'text-xl' : 'text-base'}`}
                          style={{ color: active ? 'var(--accent)' : row.locked ? '#4A4A50' : '#A0A0A8' }}
                        ></i>
                      )}
                    </div>
                    {!collapsed && (
                      <>
                        <span
                          className="ml-3 text-sm flex-1 whitespace-nowrap"
                          style={{
                            color: active ? '#fff' : row.locked ? '#4A4A50' : '#C0C0C8',
                            fontWeight: active ? 500 : 400,
                            fontFamily: 'Urbanist, Inter, sans-serif',
                          }}
                        >
                          {t(row.label as string)}
                        </span>
                        {row.count && row.count > 0 && (
                          <span className="ml-2 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}>
                            {row.count}
                          </span>
                        )}
                        {!isRd && row.locked && (
                          <i className="ri-lock-line text-xs ml-2" style={{ color: '#4A4A50' }}></i>
                        )}
                        {isGroupRow && (
                          <i
                            className="ri-arrow-down-s-line text-base ml-1 transition-transform duration-150"
                            style={{ color: '#A0A0A8', transform: open ? 'none' : 'rotate(-90deg)' }}
                          ></i>
                        )}
                      </>
                    )}
                    {collapsed && row.count && row.count > 0 && (
                      <span className="absolute top-1 right-1 text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center" style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}>
                        {row.count}
                      </span>
                    )}
                  </button>
                );
                });
              })}
            </div>
          ))}
        </nav>

        {/* Bottom user */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="p-3 shrink-0">
          <div className="flex items-center gap-3 cursor-pointer rounded-lg p-2 transition-colors hover:bg-white/5">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
              style={{ background: 'var(--accent)' }}
            >{avatarLetters}</div>
            {!collapsed && (
              <div className="overflow-hidden">
                <div className="text-sm font-medium text-white whitespace-nowrap">{displayName}</div>
                <div className="text-[11px] whitespace-nowrap" style={{ color: '#6B6B70' }}>{role}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay panel */}
      <aside
        className="fixed top-0 left-0 h-screen flex flex-col z-30 md:hidden transition-transform duration-300"
        style={{
          width: '260px',
          background: '#1A1A1A',
          borderRight: '1px solid rgba(255,255,255,0.07)',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        {/* Mobile header with close */}
        <div
          className="flex items-center justify-between h-[72px] shrink-0 px-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <img
            src="/helios-logo-white.png"
            alt="Helios AG"
            className="h-12 w-auto object-contain"
          />
          <button
            onClick={onMobileClose}
            className="w-8 h-8 flex items-center justify-center rounded cursor-pointer"
            style={{ color: '#A0A0A8' }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Mobile nav */}
        <nav className="flex-1 overflow-y-auto py-3" style={{ scrollbarWidth: 'none' }}>
          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1' : ''}>
              {section.title && (
                <div className="px-4 py-2 text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#4A4A50' }}>
                  {t(section.title as string)}
                </div>
              )}
              {section.items.flatMap((item) => {
                const group = item.children && item.children.length > 0;
                const open = group ? isGroupOpen(item) : false;
                const rows = group && open
                  ? [{ item, child: false }, ...item.children!.map((c) => ({ item: c, child: true }))]
                  : [{ item, child: false }];

                return rows.map(({ item: row, child }) => {
                const isGroupRow = row === item && group;
                const active = isActive(row.path)
                  || (isGroupRow && !open && (item.children ?? []).some((c) => isActive(c.path)));
                return (
                  <button
                    key={(child ? 'c:' : '') + row.label}
                    onClick={() => (isGroupRow ? toggleGroup(item.label) : handleNav(row.path, row.external))}
                    disabled={row.locked}
                    className="w-full flex items-center text-left transition-all duration-150 cursor-pointer relative"
                    style={{
                      padding: child ? '10px 16px 10px 34px' : '10px 16px',
                      borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
                      background: active ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
                      opacity: row.locked ? 0.45 : 1,
                    }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i
                        className={`${row.icon} text-lg`}
                        style={{ color: active ? 'var(--accent)' : row.locked ? '#4A4A50' : '#A0A0A8' }}
                      ></i>
                    </div>
                    <span
                      className="ml-3 text-sm flex-1 whitespace-nowrap"
                      style={{
                        color: active ? '#fff' : row.locked ? '#4A4A50' : '#C0C0C8',
                        fontWeight: active ? 500 : 400,
                        fontFamily: 'Urbanist, Inter, sans-serif',
                      }}
                    >
                      {t(row.label as string)}
                    </span>
                    {row.count && row.count > 0 && (
                      <span className="ml-2 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}>
                        {row.count}
                      </span>
                    )}
                    {row.locked && (
                      <i className="ri-lock-line text-xs ml-2" style={{ color: '#4A4A50' }}></i>
                    )}
                    {isGroupRow && (
                      <i
                        className="ri-arrow-down-s-line text-lg ml-1 transition-transform duration-150"
                        style={{ color: '#A0A0A8', transform: open ? 'none' : 'rotate(-90deg)' }}
                      ></i>
                    )}
                  </button>
                );
                });
              })}
            </div>
          ))}
        </nav>

        {/* Mobile bottom user */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="p-4 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ background: 'var(--accent)' }}
            >{avatarLetters}</div>
            <div className="overflow-hidden">
              <div className="text-sm font-medium text-white whitespace-nowrap">{displayName}</div>
              <div className="text-[11px] whitespace-nowrap" style={{ color: '#6B6B70' }}>{role}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}